import React from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap, Tooltip, Polyline, useMapEvent, ZoomControl, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getAllProjects, Project } from '../services/projectService';
import HlsPlayer from '../components/common/HlsPlayer';
import { globalMagazynCamUrl } from '../utils/projectCameraStreams';
import api from '../services/api';

// Ikona markera (naprawa domyślnego importu obrazków w bundlerach)
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

// Zielona ikona markera (flaga dla Magazynu)
const greenIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

// Pulsująca kropka dla pojazdów (DivIcon)
const vehicleIcon = L.divIcon({
  className: 'vehicle-pulse-icon',
  html: '<span class="vehicle-pulse"></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9]
});

type ProjectPoint = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  address?: string;
};

const normalizeAddress = (address: string) => address.trim().toLowerCase();

const getCachedGeocode = (address: string): { lat: number; lng: number } | null => {
  try {
    const key = `geo:tt:${normalizeAddress(address)}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (typeof obj?.lat === 'number' && typeof obj?.lng === 'number') return obj;
    return null;
  } catch { return null; }
};

const setCachedGeocode = (address: string, lat: number, lng: number) => {
  try {
    const key = `geo:tt:${normalizeAddress(address)}`;
    localStorage.setItem(key, JSON.stringify({ lat, lng }));
  } catch {}
};

const geocodeAddressTomTom = async (address: string, apiKey: string): Promise<{ lat?: number; lng?: number }> => {
  if (!address || !apiKey) return {};
  const cached = getCachedGeocode(address);
  if (cached) return cached;
  try {
    const url = `https://api.tomtom.com/search/2/geocode/${encodeURIComponent(address)}.json?key=${apiKey}&limit=1&countrySet=PL`;
    const res = await fetch(url);
    if (!res.ok) return {};
    const data = await res.json();
    const pos = data?.results?.[0]?.position;
    if (pos && typeof pos.lat === 'number' && typeof pos.lon === 'number') {
      setCachedGeocode(address, pos.lat, pos.lon);
      return { lat: pos.lat, lng: pos.lon };
    }
    return {};
  } catch {
    return {};
  }
};

const fallbackCenter: [number, number] = [52.2297, 21.0122]; // Warszawa

const MapsPage: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [points, setPoints] = React.useState<ProjectPoint[]>([]);
  const tomtomKey = (process.env.REACT_APP_TOMTOM_API_KEY || '').trim();
  const [editingProjectId, setEditingProjectId] = React.useState<string | null>(null);
  const [route, setRoute] = React.useState<{ coords: Array<[number, number]>; distanceKm: number; durationMin: number } | null>(null);
  const [routeFromId, setRouteFromId] = React.useState<string | null>(null);
  const [routeToId, setRouteToId] = React.useState<string | null>(null);
  const [searchMarker, setSearchMarker] = React.useState<{ lat: number; lng: number } | null>(null);
  const [nearestInfo, setNearestInfo] = React.useState<{ id: string; name: string; durationMin: number; distanceKm: number } | null>(null);
  // Traccar jest obsługiwany przez backend-proxy /api/traccar/* (HTTPS, token po stronie serwera).
  // Frontend nie zna już URL-a ani tokena Traccara.
  const [traccarEnabled, setTraccarEnabled] = React.useState<boolean>(false);
  const traccarDeviceIds = React.useMemo(() => (process.env.REACT_APP_TRACCAR_DEVICE_IDS || '')
    .split(',').map(s => s.trim()).filter(Boolean), []);
  const traccarUniqueIds = React.useMemo(() => (process.env.REACT_APP_TRACCAR_UNIQUE_IDS || '')
    .split(',').map(s => s.trim()).filter(Boolean), []); // IMEI/uniqueId
  const [resolvedDeviceIds, setResolvedDeviceIds] = React.useState<string[]>([]);
  const [deviceNames, setDeviceNames] = React.useState<Record<string, string>>({});
  const [vehicles, setVehicles] = React.useState<Array<{ id: string; name?: string; lat: number; lng: number; speed?: number; course?: number; time?: string }>>([]);
  const [activeTrack, setActiveTrack] = React.useState<{ deviceId: string; name?: string; coords: Array<[number, number]>; times?: string[]; label?: string; distCumKm?: number[]; totalKmKm?: number } | null>(null);
  const [trackLoading, setTrackLoading] = React.useState<boolean>(false);
  const [animIndex, setAnimIndex] = React.useState<number>(0);
  const [isPlaying, setIsPlaying] = React.useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = React.useState<number>(1);
  const isPlayingRef = React.useRef<boolean>(false);
  const playbackDomRef = React.useRef<{ container?: any; row0?: HTMLDivElement; btn?: HTMLButtonElement; sp?: HTMLSelectElement; range?: HTMLInputElement; ctl?: any; rowStats?: HTMLDivElement } | null>(null);
  const intervalRef = React.useRef<any>(null);
  const [settingsBump, setSettingsBump] = React.useState<number>(0);
  const [camerasOpen, setCamerasOpen] = React.useState<boolean>(false);
  const [liveClock, setLiveClock] = React.useState<string>('');
  const [isFullscreen, setIsFullscreen] = React.useState<boolean>(false);
  const camWrapperRef = React.useRef<HTMLDivElement | null>(null);
  const camTileRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const cameraTitle = 'Magazyn';
  const cameraMagazynUrls = React.useMemo(() => [1, 2, 3, 4].map((idx) => globalMagazynCamUrl(idx)), []);

  React.useEffect(() => {
    const formatClock = (d: Date) => {
      const pad = (n: number) => String(n).padStart(2, '0');
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const HH = pad(d.getHours());
      const MM = pad(d.getMinutes());
      const SS = pad(d.getSeconds());
      return `${yyyy}-${mm}-${dd}, ${HH}:${MM}:${SS}`;
    };
    if (!camerasOpen) { setLiveClock(''); return; }
    const id = setInterval(() => {
      try { setLiveClock(formatClock(new Date())); } catch {}
    }, 1000);
    try { setLiveClock(formatClock(new Date())); } catch {}
    return () => { clearInterval(id); };
  }, [camerasOpen]);

  React.useEffect(() => {
    if (!camerasOpen) return;
    const onFsChange = () => {
      try {
        const anyDoc: any = document as any;
        const fsEl = document.fullscreenElement || anyDoc.webkitFullscreenElement;
        setIsFullscreen(!!fsEl);
      } catch {}
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange as any);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange as any);
    };
  }, [camerasOpen]);

  React.useEffect(() => {
    if (!camerasOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const anyDoc: any = document as any;
        const fsEl = document.fullscreenElement || anyDoc.webkitFullscreenElement;
        if (fsEl) {
          try { document.exitFullscreen?.(); } catch {}
          try { anyDoc.webkitExitFullscreen?.(); } catch {}
          setIsFullscreen(false);
        } else {
          setCamerasOpen(false);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [camerasOpen]);
  const monthOptions = React.useMemo(() => {
    const arr: Array<{ key: string; label: string; y: number; m: number }> = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
      arr.push({ key, label, y: d.getFullYear(), m: d.getMonth() });
    }
    return arr;
  }, []);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const projects: Project[] = await getAllProjects();
        const active = projects.filter(p => p.project_phase === 'W REALIZACJI');
        const pts: ProjectPoint[] = [];
        for (const p of active) {
          const addr = (p.investment_address || '').trim();
          if (!addr) continue;
          // 1) użyj współrzędnych z DB, jeśli są
          let latLng: { lat?: number; lng?: number } = {};
          if (typeof p.location_lat === 'number' && typeof p.location_lng === 'number') {
            latLng = { lat: p.location_lat, lng: p.location_lng } as any;
          } else if (tomtomKey) {
            // 2) w przeciwnym razie geokoduj (z cache)
            latLng = await geocodeAddressTomTom(addr, tomtomKey);
            // 3) po sukcesie – zapisz do DB (best effort)
            if (latLng.lat != null && latLng.lng != null) {
              try {
                await fetch(`/api/projects/${encodeURIComponent(p.project_id)}`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ location_lat: latLng.lat, location_lng: latLng.lng, geocode_provider: 'tomtom', geocoded_at: new Date().toISOString().slice(0,19).replace('T',' ') })
                });
              } catch {}
            }
          }
          if (latLng.lat != null && latLng.lng != null) {
            pts.push({ id: p.project_id, name: p.project_name, lat: latLng.lat, lng: latLng.lng, address: addr });
          }
        }

        // Tymczasowy placeholder: jeśli nie mamy żadnych współrzędnych, pokaż 3 przykładowe pinezki (Warszawa, Kraków, Gdańsk)
        const sample: ProjectPoint[] = [
          { id: 'SAMPLE-1', name: 'Przykład – Warszawa', lat: 52.2297, lng: 21.0122 },
          { id: 'SAMPLE-2', name: 'Przykład – Kraków', lat: 50.0647, lng: 19.9450 },
          { id: 'SAMPLE-3', name: 'Przykład – Gdańsk', lat: 54.3520, lng: 18.6466 }
        ];

        if (mounted) setPoints(pts.length > 0 ? pts : sample);
      } catch (e) {
        console.error('[MapsPage] Błąd pobierania projektów:', e);
        if (mounted) setPoints([
          { id: 'SAMPLE-1', name: 'Projekt – Warszawa', lat: 52.2297, lng: 21.0122 },
          { id: 'SAMPLE-2', name: 'Projekt – Kraków', lat: 50.0647, lng: 19.9450 },
          { id: 'SAMPLE-3', name: 'Projekt – Gdańsk', lat: 54.3520, lng: 18.6466 }
        ]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const center: [number, number] = points.length ? [points[0].lat, points[0].lng] : fallbackCenter;

  const AttributionTweak: React.FC = () => {
    const map = useMap();
    React.useEffect(() => {
      try { (map as any)?.attributionControl?.setPrefix(''); } catch {}
    }, [map]);
    return null;
  };

  const ZoomOpacityControl: React.FC = () => {
    const map = useMap();
    React.useEffect(() => {
      const apply = () => {
        try {
          const z = map.getZoom();
          const el = map.getContainer();
          if (z < 10) el.classList.add('labels-faded');
          else el.classList.remove('labels-faded');
        } catch {}
      };
      apply();
      map.on('zoomend', apply);
      return () => { map.off('zoomend', apply); };
    }, [map]);
    return null;
  };

  const MapClickHandler: React.FC = () => {
    const map = useMapEvent('click', async (e) => {
      if (!editingProjectId) return;
      const { lat, lng } = e.latlng;
      // Zapis do DB
      try {
        await fetch(`/api/projects/${encodeURIComponent(editingProjectId)}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location_lat: lat, location_lng: lng, geocode_provider: 'manual', geocoded_at: new Date().toISOString().slice(0,19).replace('T',' ') })
        });
      } catch {}
      // Aktualizacja stanu lokalnego
      setPoints((prev) => prev.map(p => p.id === editingProjectId ? { ...p, lat, lng } : p));
      setEditingProjectId(null);
    });
    return null;
  };

  const fetchTomTomRoute = async (from: { lat: number; lng: number }, to: { lat: number; lng: number }) => {
    if (!tomtomKey) return null;
    const url = `https://api.tomtom.com/routing/1/calculateRoute/${from.lat},${from.lng}:${to.lat},${to.lng}/json?key=${tomtomKey}&traffic=true&routeType=fastest&travelMode=car`;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.json();
      const r = data?.routes?.[0];
      const pointsArr = (r?.legs?.[0]?.points || []).map((pt: any) => [pt.latitude, pt.longitude]) as Array<[number, number]>;
      const distanceKm = (r?.summary?.lengthInMeters || 0) / 1000;
      const durationMin = Math.round(((r?.summary?.travelTimeInSeconds || r?.summary?.durationInSeconds || 0) / 60));
      return { coords: pointsArr, distanceKm, durationMin };
    } catch {
      return null;
    }
  };

  const fetchTomTomRouteAt = async (from: { lat: number; lng: number }, to: { lat: number; lng: number }, departAtIso?: string) => {
    if (!tomtomKey) return null;
    const params = new URLSearchParams({ key: tomtomKey, traffic: 'true', routeType: 'fastest', travelMode: 'car' });
    if (departAtIso) params.set('departAt', departAtIso);
    const url = `https://api.tomtom.com/routing/1/calculateRoute/${from.lat},${from.lng}:${to.lat},${to.lng}/json?${params.toString()}`;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const data = await res.json();
      const r = data?.routes?.[0];
      const pointsArr = (r?.legs?.[0]?.points || []).map((pt: any) => [pt.latitude, pt.longitude]) as Array<[number, number]>;
      const distanceKm = (r?.summary?.lengthInMeters || 0) / 1000;
      const durationMin = Math.round(((r?.summary?.travelTimeInSeconds || r?.summary?.durationInSeconds || 0) / 60));
      return { coords: pointsArr, distanceKm, durationMin };
    } catch {
      return null;
    }
  };

  const fetchTomTomMatrixNearest = async (from: { lat: number; lng: number }, destinations: Array<{ id: string; lat: number; lng: number; name: string }>) => {
    if (!tomtomKey || destinations.length === 0) return null;
    const body = {
      origins: [{ point: { latitude: from.lat, longitude: from.lng } }],
      destinations: destinations.map(d => ({ id: d.id, point: { latitude: d.lat, longitude: d.lng } })),
      routeType: 'fastest',
      traffic: true,
      travelMode: 'car'
    } as any;
    try {
      const res = await fetch(`https://api.tomtom.com/routing/1/matrix/json?key=${tomtomKey}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      if (!res.ok) return null;
      const data = await res.json();
      const cell = data?.matrix?.[0];
      if (!Array.isArray(cell)) return null;
      let best: any = null;
      cell.forEach((c: any, idx: number) => {
        if (!c?.routeSummary) return;
        const dur = c.routeSummary.travelTimeInSeconds ?? c.routeSummary.durationInSeconds ?? Infinity;
        if (best === null || dur < best.dur) best = { idx, dur, dist: c.routeSummary.lengthInMeters };
      });
      if (!best) return null;
      const d = destinations[best.idx];
      return { id: d.id, name: d.name, durationMin: Math.round(best.dur / 60), distanceKm: (best.dist || 0) / 1000 };
    } catch { return null; }
  };

  const ClearRouteControl: React.FC = () => {
    const map = useMap();
    React.useEffect(() => {
      if (!route) return;
      const ctl = (L as any).control({ position: 'topright' });
      ctl.onAdd = () => {
        const div = L.DomUtil.create('div', 'leaflet-bar');
        const a = L.DomUtil.create('a', '', div);
        a.href = '#';
        a.title = 'Usuń trasę';
        a.innerHTML = '&times;';
        a.style.width = '28px';
        a.style.height = '28px';
        a.style.lineHeight = '28px';
        a.style.textAlign = 'center';
        a.style.fontSize = '18px';
        a.style.background = 'white';
        a.style.color = '#333';
        L.DomEvent.on(a, 'click', (e: any) => { L.DomEvent.stop(e); setRoute(null); setRouteFromId(null); setRouteToId(null); });
        return div;
      };
      ctl.addTo(map);
      return () => { try { ctl.remove(); } catch {} };
    }, [map, route]);
    return null;
  };

  const SearchControl: React.FC = () => {
    const map = useMap();
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const listRef = React.useRef<HTMLDivElement | null>(null);
    const debounceRef = React.useRef<any>(null);
    React.useEffect(() => {
      try { (map as any).zoomControl && (map as any).zoomControl.remove(); } catch {}
      const ctl = (L as any).control({ position: 'topleft' });
      ctl.onAdd = () => {
        const container = L.DomUtil.create('div', 'leaflet-control leaflet-bar');
        container.style.background = 'white';
        container.style.padding = '6px';
        container.style.borderRadius = '6px';
        container.style.boxShadow = '0 1px 4px rgba(0,0,0,0.2)';
        const input = L.DomUtil.create('input', '', container) as HTMLInputElement;
        input.type = 'text';
        input.placeholder = 'Wyszukaj adres…';
        input.style.width = '260px';
        input.style.border = '1px solid #ddd';
        input.style.padding = '6px 8px';
        input.style.borderRadius = '4px';
        inputRef.current = input;
        const list = L.DomUtil.create('div', '', container) as HTMLDivElement;
        list.style.position = 'absolute';
        list.style.marginTop = '6px';
        list.style.background = 'white';
        list.style.border = '1px solid #ddd';
        list.style.borderRadius = '4px';
        list.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        list.style.display = 'none';
        list.style.zIndex = '9999';
        listRef.current = list;
        const btn = L.DomUtil.create('button', '', container) as HTMLButtonElement;
        btn.textContent = 'Szukaj';
        btn.style.marginLeft = '6px';
        btn.style.padding = '6px 10px';
        btn.style.border = '1px solid #1976d2';
        btn.style.color = '#1976d2';
        btn.style.background = 'white';
        btn.style.borderRadius = '4px';

        // Typeahead
        L.DomEvent.on(input, 'input', () => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(async () => {
            const q = input.value.trim();
            if (!q || !tomtomKey) { list.style.display = 'none'; list.innerHTML = ''; return; }
            try {
              const url = `https://api.tomtom.com/search/2/search/${encodeURIComponent(q)}.json?key=${tomtomKey}&limit=5&typeahead=true&countrySet=PL`;
              const res = await fetch(url); if (!res.ok) return;
              const data = await res.json();
              const results = (data?.results || []).slice(0,5);
              list.innerHTML = '';
              results.forEach((r: any) => {
                const item = document.createElement('div');
                item.textContent = r.address?.freeformAddress || r.poi?.name || 'Wynik';
                (item as any).style.padding = '6px 8px';
                (item as any).style.cursor = 'pointer';
                item.onmouseenter = () => (item as any).style.background = '#f5f5f5';
                item.onmouseleave = () => (item as any).style.background = 'white';
                item.onclick = () => {
                  list.style.display = 'none';
                  input.value = item.textContent || '';
                  const pos = r.position || r.coord || r.poi?.position;
                  if (pos && typeof pos.lat === 'number' && typeof pos.lon === 'number') {
                    map.setView([pos.lat, pos.lon], 13);
                    setSearchMarker({ lat: pos.lat, lng: pos.lon });
                  }
                };
                list.appendChild(item);
              });
              list.style.display = results.length ? 'block' : 'none';
            } catch {}
          }, 200);
        });

        L.DomEvent.on(btn, 'click', async (e: any) => {
          L.DomEvent.stop(e);
          const q = input.value.trim();
          if (!q || !tomtomKey) return;
          try {
            const url = `https://api.tomtom.com/search/2/geocode/${encodeURIComponent(q)}.json?key=${tomtomKey}&limit=1&countrySet=PL`;
            const res = await fetch(url); if (!res.ok) return;
            const data = await res.json();
            const pos = data?.results?.[0]?.position;
            if (pos && typeof pos.lat === 'number' && typeof pos.lon === 'number') {
              map.setView([pos.lat, pos.lon], 13);
              setSearchMarker({ lat: pos.lat, lng: pos.lon });
            }
          } catch {}
        });

        container.appendChild(list);
        return container;
      };
      ctl.addTo(map);
      return () => { try { ctl.remove(); } catch {} };
    }, [map, tomtomKey, points, searchMarker]);
    return null;
  };

  // Sprawdzenie czy backend ma skonfigurowany Traccar (TRACCAR_URL + TRACCAR_TOKEN w backend/.env).
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/traccar/status', { suppressAuthErrorLog: true } as any);
        if (!cancelled) setTraccarEnabled(Boolean(r?.data?.configured));
      } catch {
        if (!cancelled) setTraccarEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Mapowanie IMEI (uniqueId) -> id oraz ustawienie listy urządzeń do pollingu
  React.useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (!traccarEnabled) { setResolvedDeviceIds([]); setDeviceNames({}); return; }
      try {
        const r = await api.get('/traccar/devices');
        const list = Array.isArray(r?.data) ? r.data : [];
        const names: Record<string, string> = {};
        list.forEach((d: any) => { names[String(d.id)] = d?.name || String(d.uniqueId) || String(d.id); });
        if (!cancelled) setDeviceNames(names);
        if (traccarUniqueIds.length > 0) {
          const want = new Set(traccarUniqueIds);
          const ids = list.filter((d: any) => want.has(String(d.uniqueId))).map((d: any) => String(d.id));
          if (!cancelled) setResolvedDeviceIds(ids);
        } else if (traccarDeviceIds.length > 0) {
          if (!cancelled) setResolvedDeviceIds(traccarDeviceIds);
        } else {
          // Brak filtra – pokaż wszystkie urządzenia z Traccara
          if (!cancelled) setResolvedDeviceIds(list.map((d: any) => String(d.id)));
        }
      } catch {
        if (!cancelled) { setResolvedDeviceIds([]); setDeviceNames({}); }
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [traccarEnabled, traccarUniqueIds, traccarDeviceIds]);

  React.useEffect(() => {
    if (!traccarEnabled || resolvedDeviceIds.length === 0) return;
    let stop = false;
    const fetchOnce = async () => {
      try {
        const arr: Array<{ id: string; name?: string; lat: number; lng: number; speed?: number; course?: number; time?: string }> = [];
        for (const id of resolvedDeviceIds) {
          try {
            const r = await api.get('/traccar/positions', { params: { deviceId: id } });
            const data = Array.isArray(r?.data) ? r.data : [];
            const p = data[0];
            if (p && typeof p.latitude === 'number' && typeof p.longitude === 'number') {
              arr.push({ id, name: deviceNames[id], lat: p.latitude, lng: p.longitude, speed: p.speed, course: p.course, time: p.deviceTime || p.fixTime });
            }
          } catch { /* pojedynczy device może się nie udać – pomiń */ }
        }
        if (!stop) setVehicles(arr);
      } catch {}
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 8000);
    return () => { stop = true; clearInterval(t); };
  }, [traccarEnabled, resolvedDeviceIds, deviceNames]);

  // Reset i auto-start po ustawieniu nowego śladu
  React.useEffect(() => {
    if (!activeTrack || activeTrack.coords.length < 2) return;
    setAnimIndex(0);
    setIsPlaying(true);
  }, [activeTrack?.deviceId]);

  // Synchronizacja ref z isPlaying, by unikać problemów z domknięciami
  React.useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  // Animacja odtwarzania – prosty interwał zależny od prędkości. Pauza = natychmiastowe czyszczenie interwału
  React.useEffect(() => {
    if (!activeTrack || activeTrack.coords.length < 2) return;
    if (!isPlaying) return;
    isPlayingRef.current = true;
    const total = activeTrack.coords.length;
    const paceMs = Math.max(20, Math.round(80 / Math.max(0.25, playbackSpeed)));
    const id = setInterval(() => {
      setAnimIndex(prev => {
        const next = Math.min(prev + 1, total - 1);
        if (next >= total - 1) {
          isPlayingRef.current = false;
          setIsPlaying(false);
          clearInterval(id);
        }
        return next;
      });
    }, paceMs);
    return () => { clearInterval(id); };
  }, [activeTrack?.deviceId, isPlaying, playbackSpeed]);

  // Pobranie śladu z Traccar w zadanym przedziale czasu (przez backend-proxy)
  const fetchTraccarTrack = async (deviceId: string, from: Date, to: Date) => {
    if (!traccarEnabled) return [] as Array<{ lat: number; lng: number; t?: string }>;
    try {
      const r = await api.get('/traccar/positions', {
        params: {
          deviceId: String(deviceId),
          from: new Date(from.getTime() - (from.getTimezoneOffset() * 60000)).toISOString(),
          to: new Date(to.getTime() - (to.getTimezoneOffset() * 60000)).toISOString()
        }
      });
      const data = Array.isArray(r?.data) ? r.data : [];
      const pts = data
        .filter((p: any) => typeof p.latitude === 'number' && typeof p.longitude === 'number')
        .sort((a: any, b: any) => new Date(a.deviceTime || a.fixTime || a.serverTime).getTime() - new Date(b.deviceTime || b.fixTime || b.serverTime).getTime())
        .map((p: any) => ({ lat: p.latitude, lng: p.longitude, t: p.deviceTime || p.fixTime || p.serverTime }))
        // odfiltruj dzień 2025-09-25 oraz (0,0)
        .filter((p: any) => {
          const nearZero = Math.abs(p.lat) < 0.001 && Math.abs(p.lng) < 0.001;
          if (nearZero) return false;
          if (p.t) {
            try {
              const d = new Date(p.t);
              const y = d.getUTCFullYear();
              const m = d.getUTCMonth() + 1;
              const day = d.getUTCDate();
              if (y === 2025 && m === 9 && day === 25) return false;
            } catch {}
          }
          return true;
        });
      return pts;
    } catch {
      return [];
    }
  };

  // Helpery zakresów dat
  const getTodayRange = (): { from: Date; to: Date } => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const end = new Date();
    return { from: start, to: end };
  };
  const getLast7DaysRange = (): { from: Date; to: Date } => {
    const end = new Date();
    const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    return { from: start, to: end };
  };
  const getMonthRange = (year: number, monthIndex0: number): { from: Date; to: Date } => {
    const start = new Date(year, monthIndex0, 1, 0, 0, 0, 0);
    const end = new Date(year, monthIndex0 + 1, 1, 0, 0, 0, 0);
    return { from: start, to: end };
  };

  // Ustawienia pojazdu w localStorage
  const getVehicleConsumption = (deviceId: string): number | null => {
    try { const v = localStorage.getItem(`veh:consumption:${deviceId}`); if (!v) return null; const n = parseFloat(v); return isFinite(n) ? n : null; } catch { return null; }
  };
  const getVehicleFuelPrice = (deviceId: string): number | null => {
    try { const v = localStorage.getItem(`veh:fuelPrice:${deviceId}`); if (!v) return null; const n = parseFloat(v); return isFinite(n) ? n : null; } catch { return null; }
  };
  const setVehicleSettings = (deviceId: string, consumptionLPer100?: number | null, fuelPricePln?: number | null) => {
    try {
      if (consumptionLPer100 != null) localStorage.setItem(`veh:consumption:${deviceId}`, String(consumptionLPer100));
      if (fuelPricePln != null) localStorage.setItem(`veh:fuelPrice:${deviceId}`, String(fuelPricePln));
    } catch {}
    setSettingsBump(x => x + 1);
  };

  // Obliczenia dystansu po śladzie
  const haversineKm = (a: [number, number], b: [number, number]): number => {
    const R = 6371; // km
    const toRad = (deg: number) => deg * Math.PI / 180;
    const dLat = toRad(b[0] - a[0]);
    const dLon = toRad(b[1] - a[1]);
    const lat1 = toRad(a[0]);
    const lat2 = toRad(b[0]);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return R * c;
  };
  const computeCumulativeKm = (coords: Array<[number, number]>): { cumKm: number[]; totalKm: number } => {
    const cum: number[] = [];
    let total = 0;
    for (let i = 0; i < coords.length; i++) {
      if (i === 0) cum.push(0);
      else { total += haversineKm(coords[i - 1], coords[i]); cum.push(total); }
    }
    return { cumKm: cum, totalKm: total };
  };
  const buildTrack = (deviceId: string, name: string | undefined, pts: Array<{ lat: number; lng: number; t?: string }>, label?: string) => {
    const coords = pts.map((p: { lat: number; lng: number }) => [p.lat, p.lng]) as Array<[number, number]>;
    const times = pts.map((p: { t?: string }) => p.t || '');
    const { cumKm, totalKm } = computeCumulativeKm(coords);
    return { deviceId, name, coords, times, label, distCumKm: cumKm, totalKmKm: totalKm };
  };

  // Kontrolka odtwarzania śladu na mapie (Play/Pause, suwak, prędkość)
  const TrackPlaybackControl: React.FC = () => {
    const map = useMap();
    React.useEffect(() => {
      if (!activeTrack) return;
      const ctl = (L as any).control({ position: 'bottomleft' });
      ctl.onAdd = () => {
        const container = L.DomUtil.create('div', 'leaflet-control leaflet-bar');
        container.style.background = 'white';
        container.style.padding = '8px';
        container.style.borderRadius = '6px';
        container.style.boxShadow = '0 1px 4px rgba(0,0,0,0.2)';
        container.style.minWidth = '240px';
        const fmt = (iso?: string) => {
          if (!iso) return '-';
          try { return new Date(iso).toLocaleString('pl-PL'); } catch { return iso; }
        };
        // Czas bieżącego punktu + zakres
        const row0 = L.DomUtil.create('div', '', container) as HTMLDivElement;
        row0.style.fontSize = '12px'; row0.style.color = '#333'; row0.style.marginBottom = '6px';
        const curIndex = Math.min(animIndex, activeTrack.coords.length - 1);
        const curIso = activeTrack.times?.[curIndex];
        const rangeLabel = activeTrack.label || '';
        const totalKm = activeTrack.totalKmKm || 0;
        const curKm = (activeTrack.distCumKm?.[curIndex] || 0);
        const kmLabel = `${curKm.toFixed(1)}/${totalKm.toFixed(1)} km`;
        // Spalanie i koszt (jeśli skonfigurowano w localStorage dla deviceId)
        const cons = getVehicleConsumption(activeTrack.deviceId);
        const price = getVehicleFuelPrice(activeTrack.deviceId);
        let extraHtml = '';
        if (cons != null) {
          const curL = (curKm * cons) / 100;
          const totL = (totalKm * cons) / 100;
          extraHtml += `<br/><span style="color:#333;">${curL.toFixed(1)}/${totL.toFixed(1)} L</span>`;
          if (price != null) {
            const curCost = curL * price; const totCost = totL * price;
            extraHtml += `<br/><span style="color:#333;">${Math.round(curCost)}/${Math.round(totCost)} PLN</span>`;
          }
        }
        row0.innerHTML = `Czas: ${fmt(curIso)}${rangeLabel ? ` • ${rangeLabel}` : ''}<br/><span style="color:#c62828; font-weight:600;">${kmLabel}</span>${extraHtml}`;
        const row1 = L.DomUtil.create('div', '', container) as HTMLDivElement;
        row1.style.display = 'flex'; row1.style.alignItems = 'center'; row1.style.gap = '8px';
        const btn = L.DomUtil.create('button', '', row1) as HTMLButtonElement;
        btn.textContent = isPlaying ? 'Pauza' : 'Odtwarzaj';
        btn.style.padding = '6px 10px'; btn.style.border = '1px solid #1976d2'; btn.style.color = '#1976d2';
        btn.style.background = 'white'; btn.style.borderRadius = '6px'; btn.style.cursor = 'pointer';
        btn.onclick = (e: any) => {
          L.DomEvent.stop(e);
          if (isPlayingRef.current) {
            isPlayingRef.current = false;
            setIsPlaying(false);
          } else {
            isPlayingRef.current = true;
            setIsPlaying(true);
          }
        };
        const sp = L.DomUtil.create('select', '', row1) as HTMLSelectElement;
        sp.style.flex = '0 0 auto'; sp.style.padding = '6px'; sp.style.border = '1px solid #ddd'; sp.style.borderRadius = '6px';
        ;[0.5, 1, 2, 4].forEach(v => { const opt = document.createElement('option'); opt.value = String(v); opt.text = `${v}x`; sp.appendChild(opt); });
        sp.value = String(playbackSpeed);
        sp.onchange = (e: any) => { L.DomEvent.stop(e); setPlaybackSpeed(parseFloat(sp.value) || 1); };
        const row2 = L.DomUtil.create('div', '', container) as HTMLDivElement;
        row2.style.marginTop = '8px';
        const range = L.DomUtil.create('input', '', row2) as HTMLInputElement;
        range.type = 'range'; range.min = '0'; range.max = String(Math.max(1, activeTrack.coords.length - 1));
        range.value = String(animIndex);
        range.style.width = '220px';
        range.oninput = () => { isPlayingRef.current = false; setIsPlaying(false); setAnimIndex(parseInt(range.value, 10) || 0); };
        L.DomEvent.disableClickPropagation(container);
        playbackDomRef.current = { container, row0, btn, sp, range, ctl };
        return container;
      };
      ctl.addTo(map);
      return () => { try { ctl.remove(); } catch {} playbackDomRef.current = null; };
    }, [map, activeTrack?.deviceId]);

    // Aktualizacje UI bez rekonstrukcji kontrolki
    React.useEffect(() => {
      const dom = playbackDomRef.current; if (!dom || !activeTrack) return;
      // Tekst czasu i dystansu (oraz spalanie/koszt)
      const idx = Math.min(animIndex, activeTrack.coords.length - 1);
      const curIso = activeTrack.times?.[idx];
      const totalKm = activeTrack.totalKmKm || 0;
      const curKm = (activeTrack.distCumKm?.[idx] || 0);
      const fmt = (iso?: string) => { if (!iso) return '-'; try { return new Date(iso).toLocaleString('pl-PL'); } catch { return String(iso); } };
      const kmLabel = `${curKm.toFixed(1)}/${totalKm.toFixed(1)} km`;
      let extraHtml = '';
      const cons = getVehicleConsumption(activeTrack.deviceId);
      const price = getVehicleFuelPrice(activeTrack.deviceId);
      if (cons != null) {
        const curL = (curKm * cons) / 100; const totL = (totalKm * cons) / 100;
        extraHtml += `<br/><span style="color:#333;">${curL.toFixed(1)}/${totL.toFixed(1)} L</span>`;
        if (price != null) {
          const curCost = curL * price; const totCost = totL * price;
          extraHtml += `<br/><span style=\"color:#333;\">${Math.round(curCost)}/${Math.round(totCost)} PLN</span>`;
        }
      }
      if (dom.row0) dom.row0.innerHTML = `Czas: ${fmt(curIso)}${activeTrack.label ? ` • ${activeTrack.label}` : ''}<br/><span style="color:#c62828; font-weight:600;">${kmLabel}</span>${extraHtml}`;
      // Button i select
      if (dom.btn) dom.btn.textContent = isPlayingRef.current ? 'Pauza' : 'Odtwarzaj';
      if (dom.sp && dom.sp.value !== String(playbackSpeed)) dom.sp.value = String(playbackSpeed);
      // Suwak
      if (dom.range) {
        const max = Math.max(1, activeTrack.coords.length - 1);
        if (dom.range.max !== String(max)) dom.range.max = String(max);
        if (dom.range.value !== String(animIndex)) dom.range.value = String(animIndex);
      }
    }, [animIndex, isPlaying, playbackSpeed, activeTrack?.deviceId]);
    return null;
  };

  return (
    <Box sx={{ position: 'fixed', top: { xs: 56, sm: 64 }, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
        {loading ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
          <MapContainer center={center} zoom={6} style={{ height: '100%', width: '100%' }} className="project-map" zoomControl={false}>
            <AttributionTweak />
            <ZoomOpacityControl />
            <MapClickHandler />
            <ZoomControl position="bottomright" />
            {activeTrack && <TrackPlaybackControl />}
            <SearchControl />
            <LayersControl position="topright">
              {tomtomKey ? (
                <>
                  <LayersControl.BaseLayer name="TomTom – Basic">
                    <TileLayer
                      attribution='Map data © TomTom'
                      url={`https://api.tomtom.com/map/1/tile/basic/main/{z}/{x}/{y}.png?key=${tomtomKey}`}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="TomTom – Night (ciemny)">
                    <TileLayer
                      attribution='Map data © TomTom'
                      url={`https://api.tomtom.com/map/1/tile/basic/night/{z}/{x}/{y}.png?key=${tomtomKey}`}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer checked name="TomTom – Monochrome">
                    <TileLayer
                      attribution='Map data © TomTom'
                      url={`https://api.tomtom.com/map/1/tile/basic/main/{z}/{x}/{y}.png?key=${tomtomKey}`}
                      className="tt-grayscale"
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="TomTom – Satellite">
                    <TileLayer
                      attribution='Imagery © TomTom'
                      url={`https://api.tomtom.com/map/1/tile/sat/main/{z}/{x}/{y}.jpg?key=${tomtomKey}`}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="TomTom – Hybrid (satelita + etykiety)">
                    <TileLayer
                      attribution='Imagery © TomTom'
                      url={`https://api.tomtom.com/map/1/tile/hybrid/main/{z}/{x}/{y}.png?key=${tomtomKey}`}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer name="OpenStreetMap">
                    <TileLayer
                      attribution='&copy; OpenStreetMap contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.Overlay name="Traffic flow">
                    <TileLayer
                      attribution='Traffic © TomTom'
                      url={`https://api.tomtom.com/traffic/map/4/tile/flow/absolute/{z}/{x}/{y}.png?key=${tomtomKey}`}
                      opacity={0.75}
                    />
                  </LayersControl.Overlay>
                  <LayersControl.Overlay name="Traffic incidents">
                    <TileLayer
                      attribution='Traffic © TomTom'
                      url={`https://api.tomtom.com/traffic/map/4/tile/incidents/{z}/{x}/{y}.png?key=${tomtomKey}`}
                      opacity={0.9}
                    />
                  </LayersControl.Overlay>
                </>
              ) : (
                <LayersControl.BaseLayer checked name="OpenStreetMap">
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
              )}
            </LayersControl>
            {points.map(p => {
              const isEditing = editingProjectId === p.id;
              return (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={defaultIcon} draggable={isEditing}
                eventHandlers={{
                  dragend: async (e) => {
                    const latlng = (e as any).target.getLatLng();
                    const lat = latlng.lat; const lng = latlng.lng;
                    // Zapis do DB (best effort)
                    try {
                      await fetch(`/api/projects/${encodeURIComponent(p.id)}`, {
                        method: 'PUT', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ location_lat: lat, location_lng: lng, geocode_provider: 'manual', geocoded_at: new Date().toISOString().slice(0,19).replace('T',' ') })
                      });
                    } catch {}
                    // Cache localStorage aktualizacja
                    if (p.address) { try { setCachedGeocode(p.address, lat, lng); } catch {} }
                    // Aktualizacja stanu lokalnego
                    setPoints(prev => prev.map(x => x.id === p.id ? { ...x, lat, lng } : x));
                    setEditingProjectId(null);
                  }
                }}
              >
                <Tooltip permanent direction="top" offset={[0, -24]} className="project-flag">
                  <span><b>{p.id}</b></span>
                </Tooltip>
                <Popup>
                  <div style={{ minWidth: 260, display:'grid', gap: 8 }}>
                    <div>
                      <strong>{p.id} {p.name}</strong><br />
                      {p.address ?? `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`}
                    </div>
                    <div style={{ display:'grid', gap: 6 }}>
                      <button onClick={() => setEditingProjectId(p.id)} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', cursor:'pointer' }}>{isEditing ? 'Przeciągnij pinezkę i upuść' : 'Popraw lokalizację'}</button>
                      <div>
                        <label style={{ fontSize:12, color:'#555' }}>Wyznacz trasę do:</label><br />
                        <select value={routeToId || ''} onChange={(e)=> setRouteToId(e.target.value || null)} style={{ width:'100%', padding:'6px', borderRadius:6, border:'1px solid #ddd' }}>
                          <option value="">— wybierz projekt —</option>
                          {points.filter(q => q.id !== p.id).map(q => (
                            <option key={q.id} value={q.id}>{q.id} {q.name}</option>
                          ))}
                        </select>
                        <div style={{ marginTop:6, display:'flex', gap:8 }}>
                          <button onClick={async ()=>{
                            const to = points.find(x=> x.id===routeToId || '') || null;
                            if (!to) return;
                            const r = await fetchTomTomRoute({lat:p.lat,lng:p.lng},{lat:to.lat,lng:to.lng});
                            if (r) { setRoute(r); setRouteFromId(p.id); }
                          }} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #1976d2', color:'#1976d2', background:'white', cursor:'pointer' }}>Wyznacz trasę</button>
                          <button onClick={()=>{ setRoute(null); setRouteFromId(null); setRouteToId(null); }} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', background:'white', cursor:'pointer' }}>Wyczyść</button>
                        </div>
                      </div>
                    </div>
                    {route && routeFromId===p.id && (
                      <div style={{ fontSize:12, color:'#333' }}>Długość: {route.distanceKm.toFixed(1)} km • Czas: ~{route.durationMin} min</div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );})}

            {route && (
              <Polyline positions={route.coords} pathOptions={{ color:'#1565c0', weight: 5, opacity: 0.85 }} />
            )}
            {searchMarker && (
              <CircleMarker center={[searchMarker.lat, searchMarker.lng]} pathOptions={{ color: '#d32f2f' }} radius={8} />
            )}
            {nearestInfo && searchMarker && (
              <Tooltip permanent position={[searchMarker.lat, searchMarker.lng]} className="project-flag" direction="right" offset={[12,0]}>
                <span>Najbliższy: <b>{nearestInfo.id}</b> ({nearestInfo.distanceKm.toFixed(1)} km • ~{nearestInfo.durationMin} min)</span>
              </Tooltip>
            )}
            {/* Stała flaga: Magazyn */}
            <Marker position={[52.993946, 21.862216]} icon={greenIcon}
              eventHandlers={{
                click: () => setCamerasOpen(true)
              }}
            >
              <Tooltip permanent direction="top" offset={[0, -24]} className="project-flag">
                <span><b>Magazyn</b></span>
              </Tooltip>
              <Popup>
                <div style={{ minWidth: 260, display:'grid', gap: 8 }}>
                  <div><b>Magazyn</b><br/>52.993946, 21.862216</div>
                  <div style={{ display:'grid', gap:6 }}>
                    <button onClick={() => setCamerasOpen(true)} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #1976d2', color:'#1976d2', background:'white', cursor:'pointer' }}>Kamery</button>
                  </div>
                </div>
              </Popup>
            </Marker>
            {vehicles.map(v => (
              <Marker key={`veh-${v.id}`} position={[v.lat, v.lng]} icon={vehicleIcon}>
                <Popup>
                  <div style={{ minWidth: 260, display: 'grid', gap: 8 }}>
                    <div>
                      <b>{v.name || `Pojazd ${v.id}`}</b><br/>
                      {v.time ? new Date(v.time).toLocaleString() : ''}<br/>
                      prędkość: {Math.round((v.speed || 0) * 3.6)} km/h, kurs: {Math.round(v.course || 0)}°
                    </div>
                    {/* Ustawienia pojazdu */}
                    <div style={{ borderTop:'1px solid #eee', paddingTop:8 }}>
                      <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Ustawienia pojazdu</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                        <div>
                          <label style={{ fontSize:11, color:'#666' }}>Spalanie [l/100km]</label>
                          <input type="number" step="0.1" defaultValue={getVehicleConsumption(v.id) ?? ''} id={`cons-${v.id}`}
                            style={{ width:'100%', padding:'6px', border:'1px solid #ddd', borderRadius:6 }} />
                        </div>
                        <div>
                          <label style={{ fontSize:11, color:'#666' }}>Cena brutto paliwa [PLN]</label>
                          <input type="number" step="0.01" defaultValue={getVehicleFuelPrice(v.id) ?? ''} id={`price-${v.id}`}
                            style={{ width:'100%', padding:'6px', border:'1px solid #ddd', borderRadius:6 }} />
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8, marginTop:6 }}>
                        <button onClick={() => {
                          const c = parseFloat(((document.getElementById(`cons-${v.id}`) as HTMLInputElement)?.value || '').replace(',','.'));
                          const p = parseFloat(((document.getElementById(`price-${v.id}`) as HTMLInputElement)?.value || '').replace(',','.'));
                          setVehicleSettings(v.id, isFinite(c) ? c : null, isFinite(p) ? p : null);
                        }} style={{ padding:'6px 10px', borderRadius:6, border:'1px solid #ddd', background:'white', cursor:'pointer' }}>Zapisz</button>
                      </div>
                    </div>
                    {traccarEnabled ? (
                      <div style={{ display: 'grid', gap: 6 }}>
                        <div style={{ fontSize: 12, color: '#555' }}>Pokaż trasę:</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button disabled={trackLoading}
                            onClick={async () => {
                              setTrackLoading(true);
                              const { from, to } = getTodayRange();
                              const pts = await fetchTraccarTrack(v.id, from, to);
                              setActiveTrack(pts.length ? buildTrack(v.id, v.name, pts, 'dziś') : null);
                              setTrackLoading(false);
                            }}
                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>Dziś</button>
                          <button disabled={trackLoading}
                            onClick={async () => {
                              setTrackLoading(true);
                              const { from, to } = getLast7DaysRange();
                              const pts = await fetchTraccarTrack(v.id, from, to);
                              setActiveTrack(pts.length ? buildTrack(v.id, v.name, pts, 'ostatnie 7 dni') : null);
                              setTrackLoading(false);
                            }}
                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>Ostatnie 7 dni</button>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <select id={`month-${v.id}`}
                            style={{ flex: 1, padding: '6px', borderRadius: 6, border: '1px solid #ddd' }}>
                            <option value="">— wybierz miesiąc —</option>
                            {monthOptions.map(m => (
                              <option key={m.key} value={m.key}>{m.label}</option>
                            ))}
                          </select>
                          <button disabled={trackLoading}
                            onClick={async () => {
                              const sel = (document.getElementById(`month-${v.id}`) as HTMLSelectElement | null)?.value || '';
                              if (!sel) return;
                              setTrackLoading(true);
                              const [yy, mm] = sel.split('-');
                              const y = parseInt(yy, 10); const m0 = parseInt(mm, 10) - 1;
                              const { from, to } = getMonthRange(y, m0);
                              const pts = await fetchTraccarTrack(v.id, from, to);
                              const monthLabel = new Date(y, m0, 1).toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });
                              setActiveTrack(pts.length ? buildTrack(v.id, v.name, pts, monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)) : null);
                              setTrackLoading(false);
                            }}
                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #1976d2', color: '#1976d2', background: 'white', cursor: 'pointer' }}>Pokaż</button>
                        </div>
                        {activeTrack?.deviceId === v.id && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => setActiveTrack(null)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>Wyczyść trasę</button>
                          </div>
                        )}
                        {trackLoading && <div style={{ fontSize: 12, color: '#888' }}>Ładowanie trasy…</div>}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#888' }}>Trasy dostępne po skonfigurowaniu Traccar</div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
            {activeTrack && activeTrack.coords.length > 1 && (
              <>
                <Polyline positions={activeTrack.coords.slice(0, Math.max(2, animIndex + 1))} pathOptions={{ color: '#7b1fa2', weight: 5, opacity: 0.85 }} />
                <CircleMarker center={activeTrack.coords[0]} pathOptions={{ color: '#2e7d32', fillColor: '#2e7d32' }} radius={6} />
                <CircleMarker center={activeTrack.coords[activeTrack.coords.length - 1]} pathOptions={{ color: '#c62828', fillColor: '#c62828' }} radius={6} />
                <CircleMarker center={activeTrack.coords[Math.min(animIndex, activeTrack.coords.length - 1)]} pathOptions={{ color: '#1565c0', fillColor: '#1565c0' }} radius={6} />
              </>
            )}
            
          </MapContainer>
          {camerasOpen && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 4000, pointerEvents: 'auto' }}>
              {/* Siatka niezależnych kafelków – tło przezroczyste, mapa widoczna między kafelkami */}
              <Box ref={camWrapperRef} sx={{ display:'grid', gridTemplateColumns:{ xs: '1fr 1fr', md: '1fr 1fr' }, gap:{ xs: 8, md: 12 }, width:{ xs: 'calc(100vw - 16px)', md: '50vw' } }}>
                {cameraMagazynUrls.map((u, idx) => {
                  const onTileDblClick = () => {
                    const el: any = camTileRefs.current[idx] as any;
                    if (!el) return;
                    const anyDoc: any = document as any;
                    const fsEl = document.fullscreenElement || anyDoc.webkitFullscreenElement;
                    if (!fsEl) {
                      try { el.requestFullscreen?.(); } catch {}
                      try { el.webkitRequestFullscreen?.(); } catch {}
                    } else {
                      try { document.exitFullscreen?.(); } catch {}
                      try { anyDoc.webkitExitFullscreen?.(); } catch {}
                    }
                  };
                  return (
                    <Paper key={idx} elevation={10} sx={{ width:'100%', aspectRatio:'16 / 9', borderRadius:2, overflow:'hidden', position:'relative', border:'3px solid #00e676', background:'transparent' }}>
                      <div
                        ref={(el) => { camTileRefs.current[idx] = el; }}
                        onDoubleClick={onTileDblClick}
                        style={{ position:'absolute', inset:0, background:'#000', cursor: isFullscreen ? 'zoom-out' : 'zoom-in' }}
                      >
                        <HlsPlayer src={u} autoPlay muted live style={{ width:'100%', height:'100%', objectFit:'contain', backgroundColor:'#000' }} />
                        <div style={{ position:'absolute', top:8, left:8, color:'#00e676', fontSize:12, fontWeight:700 }}>CAM{idx+1}</div>
                      </div>
                    </Paper>
                  );
                })}
              </Box>
              {/* Tytuł i zegar przy siatce */}
              <div style={{ position:'absolute', top:-6, left:-6, color:'#00e676', fontSize:14, fontWeight:700 }}>{cameraTitle}</div>
              <button onClick={()=> setCamerasOpen(false)}
                style={{ position:'absolute', top:-6, right:-6, width:28, height:28, borderRadius:14, border:'1px solid rgba(255,255,255,0.7)', background:'rgba(0,0,0,0.2)', color:'#fff', cursor:'pointer' }}>×</button>
              <div style={{ position:'absolute', bottom:-22, left:0, color:'#b9f6ca', fontSize:12, fontWeight:600 }}>{liveClock || '—'}</div>
            </Box>
          )}
          </>
        )}
    </Box>
  );
};

export default MapsPage;


